import type { MessageNode } from "./utils";
import {
  WAIT_ACTION_TOOL_NAME,
  extractMessageContent,
  getRichActionType,
  getTimelineStepSectionId,
  isHiddenCancelledAction,
  isReasoningMessage,
  isTaskToolCall,
  isSendMessageToThreadToolCall,
  isToolCallExecuting,
  isToolCallMessage,
} from "./utils";
import { extractToolCallError } from "@/modules/agents/components/helpers/richActionHelpers";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { canOpenRichActionPanel } from "@/lib/auth-utils";
import { Permission } from "@forgepoint/shared";
import { TimelineRailItem, type StepNodeState } from "./TimelineRailItem";
import { TimelineToolCallStep } from "./TimelineToolCallStep";
import { TimelineReasoningStep } from "./TimelineReasoningStep";
import { TimelineStepList } from "./TimelineStepList";
import { TimelineSubAgentStep } from "./TimelineSubAgentStep";
import { TimelineRichActionStep } from "./TimelineRichActionStep";
import { TimelineRoutedMessageStep } from "./TimelineRoutedMessageStep";

/** Per-render context shared by every step in a thread's timelines. */
export interface TimelineStepContext {
  expandedSections: Set<string>;
  onToggleSection: (sectionId: string) => void;
  showCancelled: boolean;
  isThreadActive: boolean;
  onOpenRichAction: (messageId: string) => void;
}

/** Where a step sits on the rail: first/last bound its segment, isNewest drives the green dot. */
export interface TimelineStepRail {
  isFirst: boolean;
  isLast: boolean;
  isNewest: boolean;
}

interface TimelineStepProps {
  node: MessageNode;
  ctx: TimelineStepContext;
  rail?: TimelineStepRail;
}

/**
 * Renders one timeline step, dispatching on the message kind: reasoning,
 * routed message, rich action, sub-agent task, or plain tool call. Agent
 * messages render as flat prose in the step list instead, so they never reach
 * this component. A top-level step passes `rail` to carry its state-colored
 * node; a step folded inside a work group omits it and renders bare.
 */
export function TimelineStep({ node, ctx, rail }: TimelineStepProps) {
  const currentUser = useAuthStore((state) => state.user);
  const canApproveAllTasks = useHasPermission(Permission.TASKS_MANAGE);

  const msg = node.message.message;
  const sectionId = getTimelineStepSectionId(node);
  const isExpanded = ctx.expandedSections.has(sectionId);
  const onToggle = () => ctx.onToggleSection(sectionId);

  const content = (() => {
    if (isReasoningMessage(msg)) {
      return (
        <TimelineReasoningStep
          text={extractMessageContent(msg)}
          isExpanded={isExpanded}
          onToggle={onToggle}
        />
      );
    }

    if (isSendMessageToThreadToolCall(msg)) {
      return (
        <TimelineRoutedMessageStep
          node={node}
          isExpanded={isExpanded}
          onToggle={onToggle}
          isThreadActive={ctx.isThreadActive}
        />
      );
    }

    if (!isToolCallMessage(msg) || msg.name === WAIT_ACTION_TOOL_NAME) {
      return null;
    }

    const richActionType = getRichActionType(msg);
    if (richActionType) {
      if (isHiddenCancelledAction(node, ctx.showCancelled)) {
        return null;
      }
      const error = extractToolCallError(node.toolResult);
      // Failed actions stay openable: the panel renders a dedicated error view.
      const canOpen = canOpenRichActionPanel(
        currentUser,
        node.message.task,
        canApproveAllTasks,
        richActionType,
      );
      return (
        <TimelineRichActionStep
          node={node}
          richActionType={richActionType}
          error={error}
          onOpen={
            canOpen ? () => ctx.onOpenRichAction(node.message.id) : undefined
          }
          isThreadActive={ctx.isThreadActive}
        />
      );
    }

    if (isTaskToolCall(msg.name)) {
      const description = msg.arguments.description;
      return (
        <TimelineSubAgentStep
          node={node}
          description={
            typeof description === "string" ? description : undefined
          }
          stepCount={node.children.length}
          isExpanded={isExpanded}
          onToggle={onToggle}
          isThreadActive={ctx.isThreadActive}
        >
          <TimelineStepList nodes={node.children} ctx={ctx} />
        </TimelineSubAgentStep>
      );
    }

    return (
      <TimelineToolCallStep
        node={node}
        name={msg.name}
        args={msg.arguments}
        isExpanded={isExpanded}
        onToggle={onToggle}
        isThreadActive={ctx.isThreadActive}
      />
    );
  })();

  if (!content) {
    return null;
  }

  if (!rail) {
    return content;
  }

  return (
    <TimelineRailItem
      state={getStepNodeState(node, ctx, rail.isNewest)}
      isFirst={rail.isFirst}
      isLast={rail.isLast}
      isThreadActive={ctx.isThreadActive}
    >
      {content}
    </TimelineRailItem>
  );
}

/**
 * Resolves the rail node color for a step: amber while executing, red on
 * failure, green while it is the newest step, gray once superseded.
 */
function getStepNodeState(
  node: MessageNode,
  ctx: TimelineStepContext,
  isNewest: boolean,
): StepNodeState {
  const msg = node.message.message;
  if (isToolCallMessage(msg)) {
    if (extractToolCallError(node.toolResult)) {
      return "failed";
    }
    if (isToolCallExecuting(node, ctx.isThreadActive)) {
      return "running";
    }
  }
  return isNewest ? "fresh" : "done";
}
